import { http, httpPaged, type PagedResponse } from '@/services/http';

// ── Labs ───────────────────────────────────────────────────────────────────
export interface Lab {
  id: number;
  name: string;
  emsl_lab_id?: string | null;
  address_1?: string | null;
  address_2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  telephone?: string | null;
  email?: string | null;
  /**
   * How many test methods this lab performs, per analysis category —
   * `{ ASBESTOS: 12, MICRO: 2, CHEMISTRY: 0, ... }`. Supplied by labs#index,
   * zero-filled across every category so the columns stay stable.
   *
   * A lab whose counts are all zero is invisible to clients: the capacity
   * query inner-joins lab_analysis_types.
   */
  analysis_type_counts?: Record<string, number>;
}

export const labsApi = {
  index(
    page?: number,
    perPage?: number,
    q?: string,
    sortColumn?: string,
    sortOrder?: string,
  ): Promise<PagedResponse<Lab[]> | Lab[]> {
    const params = { q, page, per_page: perPage, sort_column: sortColumn, sort_order: sortOrder };
    if (page && perPage) return httpPaged<Lab[]>({ url: 'labs', method: 'get', params });
    return http<Lab[]>({ url: 'labs', method: 'get', params });
  },
  get(id: number | string): Promise<Lab> {
    return http<Lab>({ url: `labs/${id}`, method: 'get' });
  },
  create(lab: Partial<Lab>): Promise<Lab> {
    return http<Lab>({ url: 'labs.json', method: 'post', data: lab });
  },
  update(lab: Partial<Lab> & { id: number }): Promise<Lab> {
    return http<Lab>({ url: `labs/${lab.id}`, method: 'patch', data: lab });
  },
  remove(id: number | string): Promise<void> {
    return http<void>({ url: `labs/${id}`, method: 'delete' });
  },
};

// ── Lab Orders ─────────────────────────────────────────────────────────────
export interface LabOrderStatus {
  name: 'RESERVED' | 'IN PROGRESS' | 'COMPLETED' | 'CANCELLED' | (string & {});
  datetime: string;
}

export interface LabOrderLineItem {
  quantity?: number | string;
  analysis_type?: string;
  test_method?: string;
  turnaround?: string;
}

export interface LabOrder {
  appSurveyGuid: string | null;
  templateName: string | null;
  labId: number | null;
  labName: string | null;
  reservationCode: string;
  reservedAt: string | null;
  status?: LabOrderStatus;
  orders?: LabOrderLineItem[];
  customerEmail?: string | null;
  customerName?: string | null;
  emslUserId?: string | null;
  emslBillingId?: string | null;
  erInspectorEmail?: string | null;
  erInspectorName?: string | null;
  erLabBillingId?: string | null;
  watermarked_photo_path?: string | null;
  watermarked_at?: string | null;
}

export interface LabOrderFilters {
  query?: string;
  labIds?: number[];
  statuses?: string[];
  /** ISO 8601. Server uses a default 90-day lookback if neither side is set. */
  since?: string;
  /** ISO 8601. */
  until?: string;
}

export const labOrdersApi = {
  index(
    page: number,
    perPage: number,
    filters: LabOrderFilters = {},
  ): Promise<PagedResponse<LabOrder[]>> {
    const params: Record<string, unknown> = {
      query: filters.query ?? '',
      lab_id: filters.labIds?.join(',') ?? '',
      statusFilter: filters.statuses?.join(',') ?? '',
      page,
      per: perPage,
    };
    if (filters.since) params.since = filters.since;
    if (filters.until) params.until = filters.until;
    return httpPaged<LabOrder[]>({ url: 'labOrders', method: 'get', params });
  },
  get(id: number | string): Promise<LabOrder> {
    return http<LabOrder>({ url: `labOrders/${id}`, method: 'get' });
  },
  orderCOC(appSurveyGuid: string): Promise<ArrayBuffer> {
    return http<ArrayBuffer>({
      url: 'labOrders/coc',
      method: 'get',
      responseType: 'arraybuffer',
      params: { app_survey_guid: appSurveyGuid },
    });
  },
  orderJson(appSurveyGuid: string): Promise<unknown> {
    return http({ url: 'labOrders/json', method: 'get', params: { app_survey_guid: appSurveyGuid } });
  },
};

// ── Accreditations ────────────────────────────────────────────────────────
export interface Accreditation {
  id: number;
  name: string;
  [key: string]: unknown;
}

export const accreditationsApi = {
  index(page?: number, perPage?: number, q?: string): Promise<Accreditation[]> {
    return http<Accreditation[]>({
      url: 'accreditations',
      method: 'get',
      params: { q, page, per_page: perPage },
    });
  },
  get(id: number | string) {
    return http<Accreditation>({ url: `accreditations/${id}`, method: 'get' });
  },
  create(a: Partial<Accreditation>) {
    return http<Accreditation>({ url: 'accreditations.json', method: 'post', data: a });
  },
  update(a: Partial<Accreditation> & { id: number }) {
    return http<Accreditation>({ url: `accreditations/${a.id}`, method: 'patch', data: a });
  },
  remove(id: number | string) {
    return http<void>({ url: `accreditations/${id}`, method: 'delete' });
  },
};

// ── Lab Accreditations ────────────────────────────────────────────────────
export interface LabAccreditation {
  lab: string;
  analysis_type: string;
  date: string | null;
  state: string;
  analysis_type_id: number;
  lab_id: number;
}

/** Full per-state dates for one lab + analysis type combination. */
export interface LabAccreditationRecord {
  id: number;
  lab_id: number;
  analysis_type_id: number;
  [state: string]: string | number | null;
}

/** One row in the by-lab grid — same as LabAccreditationRecord plus the analysis type name. */
export interface LabAccreditationGridRow extends LabAccreditationRecord {
  analysis_type: string;
}

export const labAccreditationsApi = {
  index() {
    return http<LabAccreditation[]>({ url: 'labAccreditations', method: 'get' });
  },
  getByLab(labId: number) {
    return http<LabAccreditationGridRow[]>({
      url: `lab_accreditations/lab/${labId}`,
      method: 'get',
    });
  },
  getRecord(labId: number, analysisTypeId: number) {
    return http<LabAccreditationRecord>({
      url: `lab_accreditations/lab/${labId}/analysis_type/${analysisTypeId}`,
      method: 'get',
    });
  },
  upsert(labId: number, analysisTypeId: number, stateMap: Record<string, string | null>) {
    return http<void>({
      url: 'lab_accreditations',
      method: 'post',
      data: {
        lab_id: labId,
        analysis_type_id: analysisTypeId,
        items: { lab_id: labId, analysis_type_id: analysisTypeId, ...stateMap },
      },
    });
  },
};

// ── Lab Capacity ──────────────────────────────────────────────────────────
export interface LabCapacityEntry {
  id: number;
  lab_id: number;
  lab: string;
  asbestos_capacity: number | null;
  asbestos_reserved: number | null;
  micro_capacity: number | null;
  micro_reserved: number | null;
  lead_capacity: number | null;
  lead_reserved: number | null;
}

export const labCapacityApi = {
  index() {
    return http<LabCapacityEntry[]>({ url: 'labCapacity', method: 'get' });
  },
  create(entry: Partial<LabCapacityEntry>) {
    return http<LabCapacityEntry>({ url: 'labCapacity', method: 'post', data: entry });
  },
  update(entry: Partial<LabCapacityEntry> & { lab_id: number }) {
    return http<LabCapacityEntry>({ url: `labCapacity/${entry.lab_id}`, method: 'patch', data: entry });
  },
  remove(labId: number) {
    return http<void>({ url: `labCapacity/${labId}`, method: 'delete' });
  },
};

// ── Analysis Types ────────────────────────────────────────────────────────
export interface AnalysisCategory {
  id: number;
  name: string;
}

export interface AnalysisType {
  id: number;
  name: string;
  category?: string | null;
  analysis_category_id?: number | null;
  [key: string]: unknown;
}

export const analysisCategoriesApi = {
  index() {
    return http<AnalysisCategory[]>({ url: 'analysisCategories', method: 'get' });
  },
};

export const analysisTypesApi = {
  index() {
    return http<AnalysisType[]>({ url: 'analysisTypes', method: 'get' });
  },
  get(id: number | string) {
    return http<AnalysisType>({ url: `analysisTypes/${id}`, method: 'get' });
  },
  create(at: Partial<AnalysisType>) {
    return http<AnalysisType>({ url: 'analysisTypes', method: 'post', data: at });
  },
  update(at: Partial<AnalysisType> & { id: number }) {
    return http<AnalysisType>({
      url: `analysisTypes/${at.id}`,
      method: 'patch',
      data: at,
    });
  },
  remove(id: number | string) {
    return http<void>({ url: `analysisTypes/${id}`, method: 'delete' });
  },
};

// ── Lab Analysis Types ────────────────────────────────────────────────────
// Which test methods a given lab performs. This is a join table, and it is not
// cosmetic: the client API's capacity query inner-joins it
// (Laboratory::Rules.base_query in emsl-api), so a lab with no rows here is
// never returned to a client for any test method.
//
// The endpoints are the ones the pre-Vue Angular modal used; only the UI was
// lost in the port. There is no update — membership is expressed by creating
// and deleting rows.
export interface LabAnalysisType {
  id: number;
  lab_id: number;
  lab_name?: string | null;
  analysis_type_id: number;
  analysis_type_name?: string | null;
}

export const labAnalysisTypesApi = {
  index(labId: number | string) {
    return http<LabAnalysisType[]>({ url: `labs/${labId}/analysis_types`, method: 'get' });
  },
  create(labId: number | string, analysisTypeId: number) {
    return http<LabAnalysisType>({
      url: `labs/${labId}/analysis_types`,
      method: 'post',
      data: { lab_id: labId, analysis_type_id: analysisTypeId },
    });
  },
  remove(labId: number | string, analysisTypeId: number) {
    return http<void>({
      url: `labs/${labId}/analysis_types/${analysisTypeId}`,
      method: 'delete',
    });
  },

  /**
   * Make the lab's assigned analysis types match `selectedIds`.
   *
   * Diffs against what the server currently holds rather than trusting a
   * client-side snapshot, so a stale dialog cannot delete rows somebody else
   * added. Returns what it changed, which is what the caller wants to report.
   *
   * Deletes run before creates purely so a failure part-way leaves the lab with
   * fewer methods than intended rather than more — under-reporting capacity is
   * the safer direction.
   */
  async sync(labId: number | string, selectedIds: number[]): Promise<{ added: number[]; removed: number[] }> {
    const current = await labAnalysisTypesApi.index(labId);
    const currentIds = current.map((r) => r.analysis_type_id);

    const wanted = new Set(selectedIds);
    const removed = currentIds.filter((id) => !wanted.has(id));
    const added = selectedIds.filter((id) => !currentIds.includes(id));

    for (const id of removed) await labAnalysisTypesApi.remove(labId, id);
    for (const id of added) await labAnalysisTypesApi.create(labId, id);

    return { added, removed };
  },
};

// ── FAQs ──────────────────────────────────────────────────────────────────
export interface Faq {
  id: number;
  question: string;
  answer: string;
  position: number;
  active: boolean;
}

export const faqsApi = {
  index() {
    return http<Faq[]>({ url: 'faqs', method: 'get' });
  },
  create(faq: Partial<Faq>) {
    return http<Faq>({ url: 'faqs', method: 'post', data: faq });
  },
  update(faq: Partial<Faq> & { id: number }) {
    return http<Faq>({ url: `faqs/${faq.id}`, method: 'patch', data: faq });
  },
  remove(id: number) {
    return http<void>({ url: `faqs/${id}`, method: 'delete' });
  },
};

// ── Tutorials ─────────────────────────────────────────────────────────────
export interface Tutorial {
  id: number;
  topic: string;
  link: string;
  position: number;
  active: boolean;
}

export const tutorialsApi = {
  index() {
    return http<Tutorial[]>({ url: 'tutorials', method: 'get' });
  },
  create(t: Partial<Tutorial>) {
    return http<Tutorial>({ url: 'tutorials', method: 'post', data: t });
  },
  update(t: Partial<Tutorial> & { id: number }) {
    return http<Tutorial>({ url: `tutorials/${t.id}`, method: 'patch', data: t });
  },
  remove(id: number) {
    return http<void>({ url: `tutorials/${id}`, method: 'delete' });
  },
};

// ── Advanced Reports ──────────────────────────────────────────────────────
export interface ReportMatrix {
  id: number;
  name: string;
  [key: string]: unknown;
}

export const advancedReportsApi = {
  matrices() {
    return http<ReportMatrix[]>({ url: 'advanced_reports/matrices', method: 'get' });
  },
  generate(matrixId: number | string, reservationCode?: string) {
    return http({
      url: 'advanced_reports/generate',
      method: 'post',
      data: { matrix_id: matrixId, reservation_code: reservationCode },
    });
  },
};
